import { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { emptyUsersGroup, UsersGroup } from "../interfaces/UsersGroup";
import { MainLayout } from "../layout/MainLayout";
import UsersGroupAdd from "./UsersGroupAdd";
import UsersGroupEdit from "./UsersGroupEdit";
import UsersGroupDelete from "./UsersGroupDelete";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../elements/LoadingSpinner";
import { handleApiError, safeJsonParse } from "../../utils/api";
import { obscureEmail } from "../../utils/utils";

export default function UsersGroups() {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const userId = localStorage.getItem('userId');
    
    const [usersGroups, setUsersGroups] = useState<UsersGroup[]>([]);
    const [selectedUsersGroup, setSelectedUsersGroup] = useState<UsersGroup>(emptyUsersGroup);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    const columns: TableColumn<UsersGroup>[] = [
        {
            name: t("Id"),
            selector: (row: UsersGroup) => row._id,
            sortable: true,
            width: '200px',
            omit: true,
        },
        {
            name: t("Name"),
            selector: (row: UsersGroup) => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: t("Members"),
            selector: (row: UsersGroup) => {
                const count = row.members?.length || 0;
                if (count === 0) return t("No members");
                return count === 1 ? t("1 member") : count + ' ' + t("members");
            },
            sortable: false,
            width: '200px',
        },
        {
            name: t("Actions"),
            cell: (row: UsersGroup) => (
                <div className="actions">
                    <a
                        className="edit-link"
                        title={t("Edit group")}
                        href="#"
                        onClick={(event) => handleShowEditForm(event, row._id)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete group")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const handleRemoveMember = (groupId: string, memberId: string) => {
        const group = usersGroups.find(g => g._id === groupId);
        if (!group) return;

        const updatedMembers = group.members.filter((m: any) => {
            const id = m.id || m.user?._id || m.user;
            return id !== memberId;
        });

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify({
                name: group.name,
                description: group.description,
                members: updatedMembers.map((m: any) => ({
                    user: m.id || m.user?._id || m.user,
                    permission: m.permission
                }))
            })
        };

        fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/' + groupId, requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setReloadData(prev => prev + 1);
            });
    };

    const ExpandedComponent = ({ data }: { data: UsersGroup }) => {
        const members = data.members || [];

        return (
            <div className="expanded-group-content">
                {data.description && (
                    <div className="group-description">
                        <strong>{t("Description")}:</strong> {data.description}
                    </div>
                )}
                <div className="group-members-list">
                    <strong>{t("Members")}:</strong>
                    {members.length === 0 ? (
                        <p className="no-members">{t("No members in this group")}</p>
                    ) : (
                        <ul>
                            {members.map((member: any, index: number) => {
                                const memberId = member.id || member.user?._id || member.user;
                                const email = member.email || member.user?.email || '';
                                const name = member.name || member.user?.profile?.name ||
                                    (member.user?.profile?.firstname && member.user?.profile?.lastname
                                        ? `${member.user.profile.firstname} ${member.user.profile.lastname}`
                                        : '');
                                const permission = member.permission || 'view';
                                const displayLabel = email ? obscureEmail(email) + (name ? ` (${name})` : '') : memberId;

                                return (
                                    <li key={`member-${memberId}-${index}`}>
                                        <span className="member-info">
                                            <span className="member-name">{displayLabel}</span>
                                            <span className={`member-permission permission-${permission}`}>
                                                {permission === 'edit' ? t("Can edit") : t("Can view")}
                                            </span>
                                        </span>
                                        <a
                                            className="remove-member"
                                            title={t("Remove from group")}
                                            onClick={() => handleRemoveMember(data._id, memberId)}
                                        ></a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        setLoading(true);
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/' + userId, requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setUsersGroups(data);
                setLoading(false);

                // Load member counts for each group
                data.forEach((group: UsersGroup) => {
                    fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/group/' + group._id + '/members', requestOptions)
                        .then(response => {
                            if (handleApiError(response, navigate)) {
                                return null;
                            }
                            return safeJsonParse(response);
                        })
                        .then(membersData => {
                            if (!membersData?.members) return;
                            // Update group with member count
                            setUsersGroups(prev => prev.map(g =>
                                g._id === group._id
                                    ? { ...g, members: membersData.members }
                                    : g
                            ));
                        });
                });
            });
    }, [reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (f: UsersGroup) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedUsersGroup: UsersGroup = usersGroups.find((o: UsersGroup) => o._id == id) ?? emptyUsersGroup;
        if (newSelectedUsersGroup._id != '') {
            setSelectedUsersGroup(newSelectedUsersGroup);
            setShowDeleteForm(true);
        } else {
            alert(t("No organization selected."));
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowEditForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedUsersGroup: UsersGroup = usersGroups.find((o: UsersGroup) => o._id == id) ?? emptyUsersGroup;
        if (newSelectedUsersGroup._id != '') {
            setSelectedUsersGroup(newSelectedUsersGroup);
            setShowEditForm(true);
        } else {
            alert(t("No organization selected."));
        }
    }
    
    const handleHideEditForm = () => {
        setShowEditForm(false);
    }

    const handleSumbitEditForm = (usersGroup: UsersGroup) => {
        setShowEditForm(false);
        setReloadData(prev => prev + 1);
    }

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    <div className="usersgroup-container">
                        <div className="page-header">
                            <h1>{ t("Users groups") }</h1>
                            <div className="page-actions">
                                <a className="action-icon" onClick={ handleShowAddForm } title={ t("New group") }>
                                    <i className="bi bi-plus-circle"></i>
                                </a>
                            </div>
                        </div>
                        <div className="usersgroup-table-container">
                            <DataTable
                                columns={columns}
                                data={usersGroups}
                                progressPending={loading}
                                progressComponent={<LoadingSpinner />}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                highlightOnHover
                                striped
                                responsive
                                noDataComponent={t("No groups found")}
                                expandableRows
                                expandableRowsComponent={ExpandedComponent}
                            />
                        </div>
                    </div>
                </div>
            </div>
            { showAddForm &&
                <UsersGroupAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            { showDeleteForm &&
                <UsersGroupDelete usersGroup={ selectedUsersGroup } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
            { showEditForm &&
                <UsersGroupEdit usersGroup={ selectedUsersGroup } submitCallback={ handleSumbitEditForm } closeCallback={ handleHideEditForm } />
            }
        </MainLayout>);
}